// @flow
import React, { Component } from 'react';
import moment from 'moment';
import Recaptcha from 'react-google-invisible-recaptcha';
import Subheader from 'material-ui/Subheader';

import Card, { CardHeader, CardText } from 'material-ui/Card';
import Avatar from 'material-ui/Avatar';
import Checkbox from 'material-ui/Checkbox';
import CircularProgress from 'material-ui/CircularProgress';
import FontIcon from 'material-ui/FontIcon';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'material-ui/TextField';
import L from 'components/Loadable';

import {
  cyan100,
  cyan500,
  cyan600,
  green100,
  green500,
  green600,
} from 'material-ui/styles/colors';

import ContactSentMessage from './ContactSentMessage';

const LoadableDateRangePicker = L(() =>
  import(/* webpackChunkName: 'hud' */
  'react-dates/lib/components/DateRangePicker')
);

const DISABLE_RECAPTCHA = process.env.FE_RECAPTCHA_DISABLED;

const FORM_FIELDS = {
  name: {
    render: true,
    type: 'texts',
    label: 'Név',
    validator: /^.+$/,
  },

  email: {
    render: true,
    type: 'email',
    label: 'E-mail cím',
    validator: /^.+@.+\..+$/,
  },

  phone: {
    render: true,
    type: 'tel',
    label: 'Telefonszám',
    validator: /^([\d +-]|\s*\(\d+\)\s*)+$/,
  },

  startDate: {
    render: true,
    type: 'daterange',
    validator: value => moment.isMoment(value) && value.isValid(),
  },

  endDate: {
    render: false,
    validator: value => moment.isMoment(value) && value.isValid(),
  },

  tosAccepted: {
    render: false,
    validator: true,
  },
};

const getStyles = ({ browser, contactCardData: { formState } }) => {
  const isSent = formState === 'sent';
  const isMobile = browser.lessThan.medium;

  return {
    card: {
      position: 'relative',
      ...(isMobile
        ? {}
        : {
            flex: 3,
            zIndex: 2,
          }),
      margin: isMobile ? 0 : '0 8px',
      overflow: 'hidden',
    },

    header: {
      background: isSent ? green100 : cyan100,
      overflow: 'hidden',
    },

    container: {
      padding: '8px 16px',
    },

    avatar: {
      backgroundColor: isSent ? green600 : cyan600,
    },

    title: {
      color: isSent ? green600 : cyan600,
      fontSize: 18,
      paddingTop: 0,
      paddingBottom: 6,
      paddingRight: 0,
    },

    subtitle: {
      color: isSent ? green500 : cyan500,
    },

    checkbox: {
      margin: '8px 0',
    },

    actionContainer: {
      textAlign: 'right',
    },

    link: {
      color: cyan600,
    },

    progressIndicator: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(255, 255, 255, .65)',
      zIndex: 10,
    },

    progressIndicatorInner: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginLeft: -40,
      marginTop: -40,
    },

    subHeader: {
      paddingLeft: 0,
    },

    datePickerContainer: {
      marginBottom: 8,
    },
  };
};

export default class ContactForm extends Component {
  state = {
    fieldsValid: Object.keys(FORM_FIELDS).reduce(
      (agg, key) => ({ ...agg, [key]: false }),
      {}
    ),
    recaptchaLoaded: !!DISABLE_RECAPTCHA,
    dateState: {
      startDate: null,
      endDate: null,
      focusedInput: null,
    },
  };

  dateValuesDirty = false;

  afterDateStateChange = () => {
    const { startDate, endDate } = this.state.dateState;
    if (!this.state.dateState.focusedInput && this.dateValuesDirty) {
      this.dateValuesDirty = false;
      this.props.setContactFields({ startDate: startDate, endDate: endDate });
      this.setState({ dateState: { ...this.state.dateState, startDate: null, endDate: null } });
    }
  };

  handleDatesChange = ({ startDate, endDate }) => {
    const { fields } = this.props.contactCardData;
    const prevStart = this.state.dateState.startDate || fields.startDate;
    const prevEnd = this.state.dateState.endDate || fields.endDate;
    this.dateValuesDirty =
      (startDate ? !startDate.isSame(prevStart) : false) ||
      (endDate ? !endDate.isSame(prevEnd) : false) ||
      this.dateValuesDirty;
    this.setState(
      { dateState: { ...this.state.dateState, startDate, endDate } },
      this.afterDateStateChange
    );
  };

  handleFocusChange = focusedInput =>
    this.setState(
      { dateState: { ...this.state.dateState, focusedInput } },
      this.afterDateStateChange
    );

  componentDidMount() {
    const { dateRange, setContactFields } = this.props;
    if (dateRange) {
      setContactFields({ startDate: dateRange.min, endDate: dateRange.max });
    }

    if (!DISABLE_RECAPTCHA) {
      this._interval = setInterval(() => {
        if (
          this._recaptcha &&
          this._recaptcha.execute &&
          !this.state.recaptchaLoaded
        ) {
          this.setState({ recaptchaLoaded: true });
          clearInterval(this._interval);
        }
      }, 500);
    }
  }

  componentWillUnmount() {
    if (!DISABLE_RECAPTCHA) {
      clearInterval(this._interval);
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      contactCardData: { fields },
    } = nextProps;

    this.setState({
      fieldsValid: Object.keys(FORM_FIELDS).reduce((agg, key) => {
        const validator = FORM_FIELDS[key].validator;
        return {
          ...agg,
          [key]:
            validator instanceof RegExp
              ? validator.test(fields[key])
              : typeof validator === 'function'
                ? validator(fields[key])
                : fields[key] === validator,
        };
      }, {}),
    });
  }

  _handleSubmit() {
    if (this.props.contactCardData.captchaResolved || DISABLE_RECAPTCHA) {
      this.props.submitContactData(
        this._recaptcha && this._recaptcha.getResponse()
      );
    } else {
      this._recaptcha.execute();
    }
  }

  renderHeader(styles) {
    const isSent = this.props.contactCardData.formState === 'sent';

    return (
      <CardHeader
        style={styles.header}
        titleStyle={styles.title}
        subtitleStyle={styles.subtitle}
        title={
          isSent ? 'Köszönjük megkeresését!' : 'Vegye fel velünk a kapcsolatot!'
        }
        subtitle={
          isSent ? (
            <span>
              Igénylése eljutott hozzánk, amellyel kapcsolatban e-mailt küldtünk
              önnek.
            </span>
          ) : (
            <span>
              Légtérigényléssel kapcsolatos díjmentes <br />
              információkért adja meg adatait
            </span>
          )
        }
        avatar={
          <Avatar
            icon={
              <FontIcon className="material-icons">
                {isSent ? 'check' : 'person_pin'}
              </FontIcon>
            }
            color="white"
            style={styles.avatar}
          />
        }
      />
    );
  }

  renderField(fieldName) {
    const {
      browser,
      contactCardData: { fields },
      setContactFields,
    } = this.props;
    const fieldDef = FORM_FIELDS[fieldName];
    const value = fields[fieldName];
    const valid = this.state.fieldsValid[fieldName];
    const styles = getStyles(this.props);

    if (fieldDef.type === 'daterange') {
      return (
        <div key={`contact-form-field-${fieldName}`}>
          <Subheader style={styles.subHeader}>Dátumtartomány</Subheader>
          <div style={styles.datePickerContainer}>
            <LoadableDateRangePicker
              startDatePlaceholderText="Kezdődátum"
              endDatePlaceholderText="Végdátum"
              startDate={this.state.dateState.startDate || fields.startDate}
              endDate={this.state.dateState.endDate || fields.endDate}
              onDatesChange={this.handleDatesChange}
              displayFormat="YYYY. MMM DD."
              renderMonth={m => m.format('YYYY. MMMM')}
              focusedInput={this.state.dateState.focusedInput}
              onFocusChange={this.handleFocusChange}
              withPortal={browser.greaterThan.small}
              withFullScreenPortal={browser.lessThan.medium}
              numberOfMonths={1}
              firstDayOfWeek={1}
              minimumNights={0}
              hideKeyboardShortcutsPanel
              isOutsideRange={() => false}
            />
          </div>
        </div>
      );
    }

    return (
      <TextField
        type={fieldDef.type}
        key={`contact-form-field-${fieldName}`}
        value={value}
        floatingLabelText={fieldDef.label}
        onChange={(_, newValue) => setContactFields({ [fieldName]: newValue })}
        errorText={
          valid || !value ? '' : `Érvénytelen ${fieldDef.label.toLowerCase()} `
        }
        fullWidth
      />
    );
  }

  renderTOSCheckbox(styles) {
    return (
      <Checkbox
        label={
          <span>
            Elfogadom az{' '}
            <a
              href="http://legter.hu/jogi-nyilatkozat/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.link}
            >
              adatkezelési feltételeket
            </a>
          </span>
        }
        labelStyle={{ zIndex: 10 }}
        style={styles.checkbox}
        checked={this.props.contactCardData.fields.tosAccepted}
        onCheck={(_, value) => {
          this.props.setContactFields({
            tosAccepted: value,
          });
        }}
      />
    );
  }

  renderCaptcha() {
    if (DISABLE_RECAPTCHA) {
      return null;
    }

    return (
      <Recaptcha
        ref={recaptcha => {
          this._recaptcha = recaptcha;
        }}
        sitekey={process.env.FE_RECAPTCHA_SITE_KEY}
        onResolved={() => {
          this.props.setContactFields({
            captchaResolved: true,
          });
          this.props.submitContactData(this._recaptcha.getResponse());
        }}
        locale="hu-HU"
      />
    );
  }

  renderActionContainer(styles) {
    const submissionAllowed =
      this.state.recaptchaLoaded &&
      Object.keys(this.state.fieldsValid)
        .map(key => this.state.fieldsValid[key])
        .every(_ => _);

    return (
      <div style={styles.actionContainer}>
        <RaisedButton
          label="Kapcsolatfelvétel"
          primary
          disabled={!submissionAllowed}
          onClick={() => this._handleSubmit()}
        />
      </div>
    );
  }

  renderContents(styles) {
    const isSent = this.props.contactCardData.formState === 'sent';

    if (!isSent) {
      return (
        <CardText style={styles.container}>
          {Object.keys(FORM_FIELDS)
            .filter(k => FORM_FIELDS[k].render)
            .map(k => this.renderField(k, FORM_FIELDS[k]))}
          {this.renderTOSCheckbox(styles)}
          {this.renderCaptcha(styles)}
          {this.renderActionContainer(styles)}

          {this.props.contactCardData.formState === 'sending' ? (
            <CircularProgress
              size={80}
              thickness={5}
              style={styles.progressIndicator}
              innerStyle={styles.progressIndicatorInner}
            />
          ) : null}
        </CardText>
      );
    }
    return (
      <ContactSentMessage
        slug={this.props.search.slug}
        toggleSearchResultsDialog={this.props.toggleSearchResultsDialog}
      />
    );
  }

  render() {
    const styles = getStyles(this.props);

    return (
      <Card style={styles.card}>
        {this.renderHeader(styles)}
        {this.renderContents(styles)}
      </Card>
    );
  }
}



// WEBPACK FOOTER //
// ./components/SearchResultsDialog/ContactForm.js